/**
 * nestLayout.js — pure layout maths for roll nesting (2026-09-18).
 *
 * The renderer uses this to draw the nest preview and to reflow sizes as the
 * user edits a card; the SAME placements (inches) are then handed to
 * python/nest_composer.py `--layout` so the printed strip is exactly what
 * was previewed. No DOM, no React, no I/O — unit-testable with node
 * (print_lab/nesting/test_nestLayout.mjs).
 *
 * Model
 *   item      { id, aspect (h/w of the SOURCE image), widthIn (placed width), rotated?: bool }
 *             rotated = the image is turned 90° on the strip (portrait ↔ landscape);
 *             its placed aspect is then 1/aspect. widthIn is always the PLACED width.
 *   placement { id, x, y, w, h, rotated }          // inches, top-left origin
 *   layout    { placements, stripWidthIn, stripHeightIn, rows, tooWide: [ids] }
 *
 * Rules (facts from printingV2/todo/06 §1.6 + Z6 hardware 2026-09-18):
 *   printable strip width per family (roll width minus what the family insets)
 *   and the extra page height some families need (Z6: +12 mm so the strip is
 *   not shrunk to fit inside the page margins).
 */

export const NEST_GAP_IN = 0.25;        // white gutter between items (cut allowance)
// Two floors (2026-09-28 bug batch #5): the automatic equal fit never goes
// below NEST_AUTO_MIN_IN (it wraps into rows instead), but a user typing a size
// may go down to NEST_MIN_ITEM_IN. Using 4" for both made ~5.2" the smallest
// typeable height on a portrait image.
export const NEST_AUTO_MIN_IN = 4;      // narrowest the auto fit makes an item
export const NEST_MIN_ITEM_IN = 1;      // narrowest a user may type (W or H)
export const NEST_MAX_ITEM_IN = 60;

/** Placed aspect (h/w) of an item, honouring a 90° rotation. */
export function placedAspect(it) {
  const a = Number(it.aspect) || 1;
  return it.rotated ? 1 / a : a;
}

export const FAMILY_STRIP_RULES = {
  T200:     { inset: 0.197, pageExtraH: 0 },      // hardware margin each side
  T600:     { inset: 0,     pageExtraH: 0 },
  T500:     { inset: 5 / 25.4,  pageExtraH: 0 },      // strip = printable width (5 mm hardware margin each side), printed 1:1
  T850:     { inset: 0,     pageExtraH: 0 },
  XT870:    { inset: 5 / 25.4,  pageExtraH: 10 / 25.4 },  // 5 mm margins in the PDF page
  Z6Z9:     { inset: 0.45,  pageExtraH: 12 / 25.4 },      // trimmer waste 0.39"/side; 6 mm T/B
  CANON_TM: { inset: 0,     pageExtraH: 0 },
};

export function stripWidthFor(family, rollWidthIn) {
  const rule = FAMILY_STRIP_RULES[family] || { inset: 0.25, pageExtraH: 0 };
  return Math.max(0, +(rollWidthIn - 2 * rule.inset).toFixed(3));
}

export function pageExtraHeightFor(family) {
  return (FAMILY_STRIP_RULES[family] || { pageExtraH: 0 }).pageExtraH;
}

const r3 = (v) => Math.round(v * 1000) / 1000;
// widths are FLOORED to a thousandth so n of them + gaps never exceed the strip by rounding
const f3 = (v) => Math.floor(v * 1000 + 1e-9) / 1000;
const EPS = 0.005;

/**
 * Equal widths: every item as wide as possible so ONE row fills the strip.
 * If that makes items narrower than NEST_AUTO_MIN_IN, use the largest equal
 * width that still respects the minimum and let reflow() wrap into rows.
 */
export function autoEqualWidths(items, stripWidthIn, gap = NEST_GAP_IN) {
  const n = items.length;
  if (n === 0) return [];
  let w = (stripWidthIn - (n - 1) * gap) / n;
  if (w < NEST_AUTO_MIN_IN) {
    const perRow = Math.max(1, Math.floor((stripWidthIn + gap) / (NEST_AUTO_MIN_IN + gap)));
    w = (stripWidthIn - (perRow - 1) * gap) / perRow;
  }
  w = Math.min(w, NEST_MAX_ITEM_IN, stripWidthIn);
  return items.map((it) => ({ ...it, widthIn: f3(w) }));
}

/**
 * Sequential shelf reflow in the items' order: place left→right, wrap to a new
 * row when the next item does not fit; a row is as tall as its tallest item.
 * Items wider than the strip are reported in `tooWide` and skipped.
 */
export function reflow(items, stripWidthIn, gap = NEST_GAP_IN) {
  const placements = [];
  const tooWide = [];
  let x = 0, y = 0, rowH = 0, rows = 0;
  for (const it of items) {
    const w = Math.max(0.01, Number(it.widthIn) || 0);
    const h = r3(w * placedAspect(it));
    if (w > stripWidthIn + EPS) { tooWide.push(it.id); continue; }
    const needX = placements.length && x > 0 ? x + gap : x;
    if (needX + w > stripWidthIn + EPS) {            // wrap
      y = y + rowH + gap;
      x = 0; rowH = 0;
    }
    const px = (x > 0) ? x + gap : x;
    placements.push({ id: it.id, x: r3(px), y: r3(y), w: r3(w), h, rotated: !!it.rotated });
    x = px + w;
    if (rowH === 0) rows += 1;
    rowH = Math.max(rowH, h);
  }
  const stripHeightIn = r3(placements.length ? y + rowH : 0);
  return { placements, stripWidthIn: r3(stripWidthIn), stripHeightIn, rows, tooWide };
}

/**
 * "Tightest fit" order (2026-09-30): tallest placed tile first, ties kept in
 * card order. Feeding reflow() this order is first-fit-decreasing-height shelf
 * packing — rows hold tiles of similar height, so less roll is left white.
 * Only the ORDER changes; sizes stay as the user set them.
 */
export function tightOrder(items) {
  return items
    .map((it, i) => ({ it, i, h: (Math.max(0.01, Number(it.widthIn) || 0)) * placedAspect(it) }))
    .sort((a, b) => (b.h - a.h) || (a.i - b.i))
    .map((x) => x.it);
}

/**
 * First-fit shelf packing (2026-10-01, "Tightest fit"). Like reflow(), but each
 * tile goes into the FIRST existing row it fits in, not only the last one — so
 * small tiles fill the white beside a tall one. Fed tightOrder() (tallest
 * first) this is FFDH: a row's height is its first tile's height. Same output
 * shape as reflow(); placements are emitted row by row, left to right.
 */
export function shelfPack(items, stripWidthIn, gap = NEST_GAP_IN) {
  const shelves = [];   // { x (used width), h, tiles: [] }
  const tooWide = [];
  for (const it of items) {
    const w = Math.max(0.01, Number(it.widthIn) || 0);
    const h = r3(w * placedAspect(it));
    if (w > stripWidthIn + EPS) { tooWide.push(it.id); continue; }
    let shelf = shelves.find((s) => s.x + gap + w <= stripWidthIn + EPS && h <= s.h + EPS);
    if (!shelf) { shelf = { x: -gap, h, tiles: [] }; shelves.push(shelf); }
    shelf.tiles.push({ id: it.id, x: shelf.x + gap, w, h, rotated: !!it.rotated });
    shelf.x += gap + w;
  }
  const placements = [];
  let y = 0;
  for (const s of shelves) {
    for (const t of s.tiles) placements.push({ id: t.id, x: r3(t.x), y: r3(y), w: r3(t.w), h: t.h, rotated: t.rotated });
    y += s.h + gap;
  }
  const stripHeightIn = r3(shelves.length ? y - gap : 0);
  return { placements, stripWidthIn: r3(stripWidthIn), stripHeightIn, rows: shelves.length, tooWide };
}

/** Share of the strip covered by images (0..1); gutters and row gaps count as waste. */
export function fillRatio(layout) {
  const area = layout.stripWidthIn * layout.stripHeightIn;
  if (!(area > 0)) return 0;
  return Math.min(1, layout.placements.reduce((s, p) => s + p.w * p.h, 0) / area);
}

// ── Free placement (2026-10-01) ───────────────────────────────────────────
// The user moves and resizes tiles on the preview; a tile is a BOX
// { id, x, y, w, h, z, rotated } in inches on the strip (origin = printable
// left edge, top). Overlap is allowed (higher z on top) and a box may hang off
// the strip — the composer clips it. When the box aspect differs from the
// image's, the image covers the box and is centre-cropped (composer `fill`).

export const NEST_MAX_BOX_IN = 120;     // free placement may exceed the strip (it is cut off)
const OFF_EPS = 0.005;

/** Centre crop of a srcW×srcH image (any unit) that covers a boxW×boxH box. Same maths as nest_composer fill. */
export function coverCrop(srcW, srcH, boxW, boxH) {
  const scale = Math.max(boxW / srcW, boxH / srcH);
  const cropW = Math.min(srcW, boxW / scale), cropH = Math.min(srcH, boxH / scale);
  return { cropX: (srcW - cropW) / 2, cropY: (srcH - cropH) / 2, cropW, cropH, scale };
}

/** Does any part of the box fall outside the printable strip (left, right or top)? The roll runs on downward. */
export function isOffPage(t, stripWidthIn) {
  return t.x < -OFF_EPS || t.y < -OFF_EPS || t.x + t.w > stripWidthIn + OFF_EPS;
}

/** Strip length = the lowest box's bottom edge (boxes above y = 0 just lose their top). */
export function stripHeightOf(tiles) {
  return r3(Math.max(0, ...tiles.map((t) => t.y + t.h)));
}

/** Paste order for the composer: lowest z first, so higher z ends on top. */
export function printOrder(tiles) {
  return tiles.map((t, i) => ({ t, i })).sort((a, b) => ((a.t.z || 0) - (b.t.z || 0)) || (a.i - b.i)).map((x) => x.t);
}

const overlaps = (a, b) => a.x < b.x + b.w - 1e-6 && b.x < a.x + a.w - 1e-6 && a.y < b.y + b.h - 1e-6 && b.y < a.y + a.h - 1e-6;

/**
 * Where a new w×h box can go without covering anything: bottom-left heuristic.
 * Candidates are (0,0) plus right-of and below every existing box (a gutter
 * away); the first one (top-most, then left-most) that fits the strip width and
 * overlaps nothing wins. Falls back to below everything.
 */
export function findFreeSpot(tiles, w, h, stripWidthIn, gap = NEST_GAP_IN) {
  const cands = [{ x: 0, y: 0 }];
  for (const t of tiles) {
    cands.push({ x: t.x + t.w + gap, y: Math.max(0, t.y) });
    cands.push({ x: Math.max(0, t.x), y: t.y + t.h + gap });
    cands.push({ x: 0, y: t.y + t.h + gap });
  }
  cands.sort((a, b) => (a.y - b.y) || (a.x - b.x));
  const inflate = (t) => ({ x: t.x - gap + 1e-6, y: t.y - gap + 1e-6, w: t.w + 2 * gap - 2e-6, h: t.h + 2 * gap - 2e-6 });
  for (const c of cands) {
    const box = { x: c.x, y: c.y, w, h };
    if (c.x + w > stripWidthIn + EPS && c.x > 0) continue;
    if (tiles.some((t) => overlaps(box, inflate(t)))) continue;
    return { x: r3(c.x), y: r3(c.y) };
  }
  return { x: 0, y: r3(stripHeightOf(tiles) + (tiles.length ? gap : 0)) };
}

/** Share of the strip covered by the VISIBLE union of boxes (overlaps counted once, off-strip parts ignored). */
export function visibleFill(tiles, stripWidthIn, stripHeightIn) {
  const area = stripWidthIn * stripHeightIn;
  if (!(area > 0) || !tiles.length) return 0;
  const clip = tiles.map((t) => ({
    x0: Math.max(0, t.x), y0: Math.max(0, t.y),
    x1: Math.min(stripWidthIn, t.x + t.w), y1: Math.min(stripHeightIn, t.y + t.h),
  })).filter((r) => r.x1 > r.x0 && r.y1 > r.y0);
  const xs = [...new Set(clip.flatMap((r) => [r.x0, r.x1]))].sort((a, b) => a - b);
  const ys = [...new Set(clip.flatMap((r) => [r.y0, r.y1]))].sort((a, b) => a - b);
  let covered = 0;
  for (let i = 0; i < xs.length - 1; i++) {
    for (let j = 0; j < ys.length - 1; j++) {
      const cx = (xs[i] + xs[i + 1]) / 2, cy = (ys[j] + ys[j + 1]) / 2;
      if (clip.some((r) => cx > r.x0 && cx < r.x1 && cy > r.y0 && cy < r.y1)) covered += (xs[i + 1] - xs[i]) * (ys[j + 1] - ys[j]);
    }
  }
  return Math.min(1, covered / area);
}

const toBoxes = (layout) => layout.placements.map((p) => ({ id: p.id, x: p.x, y: p.y, w: p.w, h: p.h }));

/**
 * Auto arrange "Equal sizes": every item the same width at its natural (placed)
 * aspect, packed tallest-first. items = [{ id, aspect, rotated }]. Crops reset.
 */
export function arrangeEqual(items, stripWidthIn, gap = NEST_GAP_IN) {
  const eq = autoEqualWidths(items, stripWidthIn, gap);
  return toBoxes(shelfPack(tightOrder(eq), stripWidthIn, gap));
}

/**
 * Auto arrange "Tightest fit": keep every box's size (a box wider than the
 * strip is shrunk to the strip, aspect kept) and repack tallest-first.
 * boxes = [{ id, w, h }].
 */
export function arrangeTight(boxes, stripWidthIn, gap = NEST_GAP_IN) {
  const items = boxes.map((b) => {
    const w = Math.min(b.w, stripWidthIn);
    return { id: b.id, aspect: b.h / b.w, widthIn: f3(w) };
  });
  return toBoxes(shelfPack(tightOrder(items), stripWidthIn, gap));
}
